// components/CompletionRing.tsx
import React from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ChoreStatus } from '../domain';
import { cx } from './ui';

const RADIUS = 14.5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const STATUS_RING: Record<ChoreStatus, { stroke: string; hover: string; text: string }> = {
  [ChoreStatus.Overdue]: { stroke: 'stroke-status-overdue dark:stroke-red-400', hover: 'hover:bg-status-overdue-soft dark:hover:bg-red-500/15', text: 'text-status-overdue dark:text-red-400' },
  [ChoreStatus.DueToday]: { stroke: 'stroke-status-today dark:stroke-amber-400', hover: 'hover:bg-status-today-soft dark:hover:bg-amber-500/15', text: 'text-status-today dark:text-amber-400' },
  [ChoreStatus.Upcoming]: { stroke: 'stroke-status-upcoming dark:stroke-indigo-400', hover: 'hover:bg-status-upcoming-soft dark:hover:bg-indigo-500/15', text: 'text-status-upcoming dark:text-indigo-400' },
};

interface CompletionRingProps {
  status: ChoreStatus;
  /** 0–1: how far through its interval the chore is; the ring fills as it gets closer to due. */
  progress: number;
  /** Shows the tick, for the moment between the tap and the chore moving on. */
  done: boolean;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * The chore's done button: a ring that fills up towards the due date, coloured by status, with a
 * tick on hover and once tapped. Tapping it marks the chore done.
 */
export const CompletionRing: React.FC<CompletionRingProps> = ({ status, progress, done, label, disabled, onClick }) => {
  const look = STATUS_RING[status];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || done}
      aria-label={label}
      title={label}
      className={cx(
        'group/ring relative flex h-9 w-9 flex-none items-center justify-center rounded-full transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
        'disabled:cursor-not-allowed',
        done ? 'bg-success text-white' : cx(look.text, !disabled && look.hover),
      )}
    >
      <svg viewBox="0 0 36 36" className="absolute inset-0 -rotate-90" aria-hidden="true">
        <circle cx="18" cy="18" r={RADIUS} fill="none" strokeWidth="3" className="stroke-gray-200 dark:stroke-gray-700" />
        <circle
          cx="18"
          cy="18"
          r={RADIUS}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={done ? 0 : CIRCUMFERENCE * (1 - progress)}
          className={cx(
            'transition-[stroke-dashoffset] duration-500 ease-out motion-reduce:transition-none',
            done ? 'stroke-success dark:stroke-emerald-400' : look.stroke,
          )}
        />
      </svg>
      <CheckIcon
        aria-hidden="true"
        className={cx(
          'relative h-4 w-4 transition duration-150 motion-reduce:transition-none',
          done
            ? 'scale-100 opacity-100'
            : 'scale-75 opacity-0 group-hover/ring:scale-100 group-hover/ring:opacity-90 group-focus-visible/ring:scale-100 group-focus-visible/ring:opacity-90',
          disabled && !done && 'group-hover/ring:opacity-0',
        )}
      />
    </button>
  );
};

export default CompletionRing;
